import type { TemplateListDto } from '@carbonwebmail/contracts'
import type { ReactNode } from 'react'
import { Alert } from '../../ui/Alert.js'
import { audienceOptions } from '../../ui/audienceOptions.js'
import { BodyEditor } from '../../ui/BodyEditor.js'
import { Field, SelectField, TextAreaField } from '../../ui/Field.js'
import { MessageFrame } from '../../ui/MessageFrame.js'
import type { useNewCampaign } from './useNewCampaign.js'

/**
 * The five steps, each asking one thing.
 *
 * Separated from the dialog that frames them because the frame is about
 * sequence — which step, what is blocking it, how to leave — and these are
 * about the questions. Keeping both in one file meant the step navigation was
 * four hundred lines away from the button that drives it.
 *
 * **Every suggestion is a button, never a value that appeared.** A field that
 * fills itself while somebody is reading it is a field they have to check, and
 * checking it costs more than typing it would have. The one exception is the
 * campaign name, and that is in the view model with its reason.
 */

type Wizard = ReturnType<typeof useNewCampaign>

/** A heading and a sentence saying why the step is being asked. */
function Asks({ children }: { readonly children: ReactNode }) {
  return <p className="mb-5 text-[13px] text-t2 leading-relaxed">{children}</p>
}

/**
 * The button that asks the model, and what it says while it is asking.
 *
 * One component because the two places it appears — the brief and the subject
 * lines — are the same press against the same answer, and two copies would be
 * two chances for one of them to stay enabled while a request was in flight.
 */
function SuggestButton({
  plan,
  disabled,
  label,
  onAsk,
}: {
  readonly plan: Wizard['plan']
  readonly disabled: boolean
  readonly label: string
  readonly onAsk: () => void
}) {
  const thinking = plan.status === 'thinking'

  return (
    <button
      type="button"
      disabled={disabled || thinking}
      onClick={onAsk}
      className="inline-flex items-center gap-2 rounded-pill border border-brand/40 bg-brand/5 px-4 py-2 font-bold text-[13px] text-brand-ink transition-colors hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {/* Not an emoji: there is no emoji font in every browser this runs in,
          and a tofu box beside the one AI affordance is worse than no icon. */}
      <span aria-hidden="true">✧</span>
      {thinking ? 'Thinking…' : label}
    </button>
  )
}

export function PurposeStep({ wizard }: { readonly wizard: Wizard }) {
  const { draft, plan, change, suggest } = wizard

  return (
    <>
      <Asks>
        Two lines is plenty. Nobody outside your organisation reads this — it is what the
        suggestions below are worked out from.
      </Asks>

      <TextAreaField
        id="campaign-brief"
        label="What is this email about?"
        rows={3}
        prose
        value={draft.brief}
        disabled={false}
        hint="For example: our Black Friday sale, 40% off everything, ends Sunday night."
        onChange={(value) => change('brief', value)}
      />

      <div className="-mt-2 mb-5">
        <SuggestButton
          plan={plan}
          disabled={draft.brief.trim() === ''}
          label={plan.status === 'ready' ? 'Suggest again' : 'Suggest a name and subject lines'}
          onAsk={() => void suggest()}
        />
        {plan.status === 'none' ? (
          <p className="mt-2 text-[12px] text-t3">
            No suggestions this time. Carry on — everything below works without them.
          </p>
        ) : null}
      </div>

      <Field
        id="campaign-name"
        label="Campaign name"
        type="text"
        autoComplete="off"
        value={draft.name}
        disabled={false}
        hint="Only you see this. It names the campaign in this list and in its report."
        onChange={(value) => change('name', value)}
      />
    </>
  )
}

export function AudienceStep({ wizard }: { readonly wizard: Wizard }) {
  const { draft, segments, plan, change } = wizard
  const audiences = audienceOptions(segments)

  /*
   * A suggestion is only offered while it is still a suggestion.
   *
   * Once the audience already is what the model picked, the row would be a
   * button that does nothing — and a row saying "we suggest X" above a field
   * reading X reads as though the screen has not noticed.
   */
  const suggested =
    plan.status === 'ready' &&
    plan.plan.segmentId !== null &&
    plan.plan.segmentId !== draft.segmentId
      ? audiences.find(([value]) => value === plan.plan.segmentId)
      : undefined

  return (
    <>
      <Asks>
        Worked out at the moment you send, not now. Anyone who unsubscribes between here and then is
        left out of it.
      </Asks>

      {/* Not fatal — every campaign can still go to the whole list — but said,
          because offering one choice where there should be several without
          explaining why is a lie about what is available. */}
      {segments.status === 'failed' ? <Alert>{segments.detail}</Alert> : null}

      {suggested === undefined ? null : (
        <div className="mb-5 rounded-card border border-brand/30 bg-brand/5 p-4">
          <p className="font-bold text-[13px] text-t1">
            <span aria-hidden="true">✧</span> From your brief, this one looks right
          </p>
          <p className="mt-1 text-[13px] text-t2">{plan.status === 'ready' ? plan.plan.why : ''}</p>
          <button
            type="button"
            onClick={() => change('segmentId', suggested[0])}
            className="mt-3 rounded-pill bg-brand px-4 py-2 font-bold text-[13px] text-brand-contrast"
          >
            Use “{suggested[1]}”
          </button>
        </div>
      )}

      <SelectField
        id="campaign-audience"
        label="Audience"
        value={draft.segmentId}
        disabled={segments.status === 'loading'}
        options={audiences}
        hint="Everyone subscribed is a real audience, not a fallback. It is also the commonest answer."
        onChange={(value) => change('segmentId', value)}
      />
    </>
  )
}

export function SubjectStep({ wizard }: { readonly wizard: Wizard }) {
  const { draft, plan, change, suggest } = wizard
  const offered = plan.status === 'ready' ? plan.plan.subjects : []

  return (
    <>
      <Asks>
        This and the sender name are the whole of what somebody sees before deciding whether to open
        it. Under sixty characters stays whole on a phone.
      </Asks>

      {offered.length === 0 ? (
        <div className="mb-5">
          <SuggestButton
            plan={plan}
            disabled={draft.brief.trim() === ''}
            label="Suggest three subject lines"
            onAsk={() => void suggest()}
          />
        </div>
      ) : (
        <div className="mb-5">
          <p className="mb-2 font-bold text-[12px] text-t3 uppercase tracking-wide">
            <span aria-hidden="true">✧</span> Suggestions
          </p>
          <ul className="space-y-2">
            {offered.map((line) => (
              <li key={line}>
                <button
                  type="button"
                  onClick={() => change('subject', line)}
                  className={`w-full rounded-card border px-4 py-3 text-left text-[14px] transition-colors ${
                    draft.subject === line
                      ? 'border-brand bg-brand/5 font-bold text-t1'
                      : 'border-line bg-surface text-t2 hover:border-brand/40 hover:text-t1'
                  }`}
                >
                  {line}
                  <span className="ml-2 text-[12px] text-t3">{line.length} characters</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Field
        id="campaign-subject"
        label="Subject"
        type="text"
        autoComplete="off"
        value={draft.subject}
        disabled={false}
        hint="Pick one above, or write your own — whatever is in this box is what sends."
        onChange={(value) => change('subject', value)}
      />
    </>
  )
}

/** One of the three ways to end up with a message. */
function WayIn({
  title,
  detail,
  featured,
  disabled,
  onPick,
}: {
  readonly title: string
  readonly detail: string
  readonly featured?: boolean | undefined
  readonly disabled?: boolean | undefined
  readonly onPick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled === true}
      onClick={onPick}
      className={`flex-1 rounded-card border p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        featured === true
          ? 'border-brand/40 bg-brand/5 hover:bg-brand/10'
          : 'border-line bg-surface hover:border-brand/40'
      }`}
    >
      <p className="font-bold text-[14px] text-t1">
        {featured === true ? <span aria-hidden="true">✧ </span> : null}
        {title}
      </p>
      <p className="mt-1 text-[12px] text-t2 leading-relaxed">{detail}</p>
    </button>
  )
}

export function MessageStep({ wizard }: { readonly wizard: Wizard }) {
  const { draft, templates, design, change, applyTemplate, designFromBrief } = wizard

  const designed = draft.body.kind === 'designed'
  const ready =
    templates.status === 'ready' ? templates.value.templates : ([] as TemplateListDto['templates'])

  return (
    <>
      <Asks>Three ways in. Whichever you pick, you can keep editing afterwards.</Asks>

      <div className="mb-5 flex flex-wrap gap-3">
        <WayIn
          featured
          title="Design it with AI"
          detail="Uses the brief from the first step. Comes back as ordinary blocks you can edit."
          disabled={draft.brief.trim() === '' || design.status === 'designing'}
          onPick={() => void designFromBrief()}
        />
        <WayIn
          title="Start from a template"
          /*
            The copy is one-way, and that is said on the card rather than under
            the picker below it. The picker only appears when there is more than
            one template to choose between, so saying it there meant an account
            with exactly one template was never told.
          */
          detail={
            ready.length === 0
              ? 'You have not saved any templates yet.'
              : `Copies one of your ${ready.length} saved designs. Editing the template later will not change this campaign.`
          }
          disabled={ready.length === 0}
          onPick={() => {
            const first = ready[0]
            if (first !== undefined) applyTemplate(first.templateId)
          }}
        />
        <WayIn
          title="Write it myself"
          detail="A plain message, typed into the box below."
          onPick={() => change('body', { kind: 'prose', text: '' })}
        />
      </div>

      {design.status === 'designing' ? (
        <p className="mb-5 rounded-card border border-line bg-surface-2 px-4 py-3 text-[13px] text-t2">
          Designing from your brief. This takes a few seconds.
        </p>
      ) : null}
      {design.status === 'failed' ? <Alert>{design.why}</Alert> : null}

      {/*
        A template picker, once a template has put something in the box. Offered
        only when there is something to offer: an empty picker labelled "start
        from a template" is a control that teaches people the feature does not
        work.
      */}
      {ready.length > 1 && !designed ? (
        <SelectField
          id="campaign-template"
          label="Template"
          value=""
          options={[
            ['', 'Choose a template…'],
            ...ready.map((one) => [one.templateId, one.name] as const),
          ]}
          hint="Copies its message, and its subject when you have not chosen one already."
          onChange={applyTemplate}
        />
      ) : null}

      {designed ? (
        <div>
          <p className="mb-2 font-bold text-[12px] text-t3 uppercase tracking-wide">Preview</p>
          {/* The design as it came back, in the same sandboxed frame every
              other preview uses. Not the server-rendered preview: that wraps a
              body in the sending shell and needs a campaign to wrap it for,
              and there is no campaign until the last step. */}
          <MessageFrame
            className="h-[300px] rounded-field"
            device="desktop"
            html={draft.body.html}
          />
          <p className="mt-2 text-[12px] text-t3">
            Saved as a draft, this opens in the block composer where every part of it can be
            changed.
          </p>
        </div>
      ) : (
        /* No Compose from here: there is no campaign yet to compose. The wizard
           saves a draft and lands in the composer, where the link is real. */
        <BodyEditor
          id="campaign-body"
          body={draft.body}
          disabled={false}
          composeAt=""
          onChange={(value) => change('body', value)}
        />
      )}
    </>
  )
}

/** One line of the summary: what it is called, and what it says. */
function Line({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex gap-4 border-line border-b py-2.5 last:border-b-0">
      <dt className="w-32 flex-none text-[13px] text-t3">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-[13px] text-t1">{children}</dd>
    </div>
  )
}

export function ReviewStep({ wizard }: { readonly wizard: Wizard }) {
  const { draft, segments, change } = wizard
  const audiences = audienceOptions(segments)
  const audience =
    audiences.find(([value]) => value === draft.segmentId)?.[1] ?? 'Everyone subscribed'

  return (
    <>
      <Asks>
        Nothing is sent by creating this. It is saved as a draft, and sending is a separate press on
        the campaign itself.
      </Asks>

      <dl className="mb-6 rounded-card border border-line bg-surface px-4 py-1">
        <Line label="Name">{draft.name}</Line>
        <Line label="Audience">{audience}</Line>
        <Line label="Subject">{draft.subject}</Line>
        <Line label="Message">
          {draft.body.kind === 'designed' ? 'A design' : 'A plain message'}
        </Line>
      </dl>

      {/*
        The sender is asked for last and prefilled from the organisation's
        settings, because it is the one group of fields that is the same on
        every campaign. Editable here rather than on a step of its own: a step
        somebody passes through without changing anything is a step that should
        have been a row on this page.
      */}
      <Field
        id="campaign-from-name"
        label="From name"
        type="text"
        autoComplete="off"
        value={draft.fromName}
        disabled={false}
        hint="Who it appears to be from — usually your organisation's name."
        onChange={(value) => change('fromName', value)}
      />

      <Field
        id="campaign-from-email"
        label="From address"
        type="email"
        autoComplete="off"
        value={draft.fromEmail}
        disabled={false}
        hint="Must be on a domain you have verified, or the mail goes to spam."
        onChange={(value) => change('fromEmail', value)}
      />

      <Field
        id="campaign-reply-to"
        optional
        label="Reply-to address"
        type="email"
        autoComplete="off"
        value={draft.replyTo}
        disabled={false}
        hint="Where a reply goes. Left empty, replies go to the from address."
        onChange={(value) => change('replyTo', value)}
      />
    </>
  )
}
